import { useMutation } from '@apollo/client'
import { Formik, FormikHelpers } from 'formik'
import { isEmpty } from 'lodash'
import { ReactElement, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { Button } from 'components/v2/Button'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import FormLayout from 'components/v2/FormLayout'
import { PartnerDataFormCard } from 'features/Partners/components/PartnerDataFormCard'
import { TaxComplianceFormCard } from 'features/Partners/components/TaxComplianceFormCard'
import { INewPartnerStep1 } from 'features/Partners/types'
import {
  mapPartnerDetailsFormToMutation,
  mapPartnerDetailsResponseToMutation,
  mapTaxInformationFormToMutation,
  mapTaxInformationResponseToMutation
} from 'features/Partners/utils'
import {
  ADD_PARTNER_DOCUMENTS,
  CHANGE_PARTNER_DETAILS,
  CHANGE_PARTNER_TAX_INFORMATION,
  CREATE_PARTNER,
  REMOVE_PARTNER_DOCUMENTS
} from 'features/Partners/mutations-legacy'
import {
  GTAddPartnerDocumentsInput,
  GTCreatePartnerResponse,
  GTBusinessPartner,
  GTPartnerDocumentCategoryInput,
  GTBusinessPartnerDocumentCategory,
  GTBusinessPartnerVertical
} from 'gql/types.generated'
import { messages as fileMessages } from 'messages/file'
import { getUnprocessableErrors } from 'utils/error'
import { usePushFileToS3 } from 'utils/file'
import { schema } from './schema'
import { useSearchParams } from 'react-router-dom'
import { getStateByStateCode } from 'utils/states'
import { getDifference } from 'utils/object'

export const NewPartnerStep1 = ({
  partner,
  isEdit,
  onSubmit,
  onCancel
}: {
  partner?: GTBusinessPartner
  isEdit?: boolean
  onSubmit: () => void
  onCancel: () => void
}): ReactElement => {
  const intl = useIntl()
  const [searchParams, setSearchParams] = useSearchParams()
  const [s3Error, setS3Error] = useState<string | undefined>()

  const [createPartner, { error: addPartnerDataError }] = useMutation<{ createPartner: GTCreatePartnerResponse }>(
    CREATE_PARTNER
  )
  const [changePartnerDetails, { error: changePartnerDetailsError }] = useMutation(CHANGE_PARTNER_DETAILS)
  const [changePartnerTaxInformation, { error: changePartnerTaxInformationError }] =
    useMutation(CHANGE_PARTNER_TAX_INFORMATION)
  const [addPartnerDocuments] = useMutation(ADD_PARTNER_DOCUMENTS)
  const [removePartnerDocuments] = useMutation(REMOVE_PARTNER_DOCUMENTS)
  const pushFileToS3 = usePushFileToS3()

  const handleCreateSubmit = async (
    values: INewPartnerStep1,
    { setSubmitting }: FormikHelpers<INewPartnerStep1>
  ): Promise<void> => {
    const createPartnerInput = mapPartnerDetailsFormToMutation(values)
    try {
      let partnerId = searchParams.get('partnerId')
      if (!partnerId) {
        const { data } = await createPartner({
          variables: {
            input: createPartnerInput
          }
        })
        if (!data?.createPartner.id) {
          throw new Error('Partner ID not returned')
        }
        partnerId = data.createPartner.id as string
        setSearchParams({ partnerId }, { replace: true })
      }
      const changePartnerTaxInformationInput = mapTaxInformationFormToMutation(values)
      await changePartnerTaxInformation({
        variables: {
          id: partnerId,
          input: changePartnerTaxInformationInput
        }
      })
      try {
        let curpFilePath: string, taxIdFilePath: string
        const addPartnerDocumentsInput: GTAddPartnerDocumentsInput = {
          documents: []
        }
        if (values.curpDocument?.file) {
          taxIdFilePath = await pushFileToS3(values.taxIdDocument?.file as File)
          addPartnerDocumentsInput.documents.push({
            category: GTPartnerDocumentCategoryInput.Rfc,
            filePath: taxIdFilePath
          })
        }

        if (values.curpDocument?.file) {
          curpFilePath = await pushFileToS3(values.curpDocument.file)
          addPartnerDocumentsInput.documents.push({
            category: GTPartnerDocumentCategoryInput.Curp,
            filePath: curpFilePath
          })
        }

        if (!isEmpty(addPartnerDocumentsInput.documents)) {
          await addPartnerDocuments({
            variables: {
              id: partnerId,
              input: addPartnerDocumentsInput
            }
          })
        }
      } catch (ex) {
        setS3Error(intl.formatMessage(fileMessages.uploadFileError, { count: 1 }))
        return
      }
      onSubmit()
    } catch (ex) {
      // DO NOTHING
    }
    setSubmitting(false)
  }

  const handleEditSubmit = async (
    values: INewPartnerStep1,
    { setSubmitting }: FormikHelpers<INewPartnerStep1>
  ): Promise<void> => {
    try {
      const oldPartnerDetails = mapPartnerDetailsResponseToMutation(partner as GTBusinessPartner)
      const newPartnerDetails = mapPartnerDetailsFormToMutation(values)
      const partnerDetailsDiff = getDifference(newPartnerDetails, oldPartnerDetails)
      if (!isEmpty(partnerDetailsDiff)) {
        await changePartnerDetails({
          variables: {
            id: partner?.id,
            input: newPartnerDetails
          }
        })
      }

      const oldTaxInformation = mapTaxInformationResponseToMutation(partner as GTBusinessPartner)
      const newTaxInformation = mapTaxInformationFormToMutation(values)
      const taxInformationDiff = getDifference(newTaxInformation, oldTaxInformation)
      if (!isEmpty(taxInformationDiff)) {
        await changePartnerTaxInformation({
          variables: {
            id: partner?.id,
            input: newTaxInformation
          }
        })
      }
      try {
        let curpFilePath
        let taxIdFilePath
        const removedDocumentsIds: string[] = []
        const addPartnerDocumentsInput: GTAddPartnerDocumentsInput = {
          documents: []
        }

        if (values.taxIdDocument?.file) {
          taxIdFilePath = await pushFileToS3(values.taxIdDocument.file as File)
          if (taxIdDocument?.id) {
            removedDocumentsIds.push(taxIdDocument?.id)
          }
          addPartnerDocumentsInput.documents.push({
            category: GTPartnerDocumentCategoryInput.Rfc,
            filePath: taxIdFilePath
          })
        }
        const isRemovingCurp = curpDocument?.id && !values.curpDocument?.id
        if (isRemovingCurp) {
          removedDocumentsIds.push(curpDocument?.id)
        }
        if (values.curpDocument?.file) {
          curpFilePath = await pushFileToS3(values.curpDocument.file)
          addPartnerDocumentsInput.documents.push({
            category: GTPartnerDocumentCategoryInput.Curp,
            filePath: curpFilePath
          })
        }
        if (removedDocumentsIds.length > 0) {
          await removePartnerDocuments({
            variables: {
              id: partner?.id,
              input: {
                documentIDs: removedDocumentsIds
              }
            }
          })
        }
        if (addPartnerDocumentsInput.documents.length > 0) {
          await addPartnerDocuments({
            variables: {
              id: partner?.id,
              input: addPartnerDocumentsInput
            }
          })
        }
      } catch (ex) {
        setS3Error(intl.formatMessage(fileMessages.uploadFileError, { count: 1 }))
        return
      }

      onSubmit()
    } catch (ex) {
      // DO NOTHING
    }
    setSubmitting(false)
  }

  const taxIdDocument = partner?.documents?.find(({ category }) => category === GTBusinessPartnerDocumentCategory.Rfc)
  const curpDocument = partner?.documents?.find(({ category }) => category === GTBusinessPartnerDocumentCategory.Curp)
  const initialValues: INewPartnerStep1 = {
    name: partner?.name ?? '',
    city: partner?.city
      ? {
          id: partner.city.id,
          name: partner.city.name,
          stateCode: partner.city.stateCode
        }
      : undefined,
    state: partner?.stateCode
      ? getStateByStateCode(partner.stateCode)
      : {
          code: '',
          name: ''
        },
    verticals: partner?.verticals || [
      GTBusinessPartnerVertical.City,
      GTBusinessPartnerVertical.Team,
      GTBusinessPartnerVertical.Rental
    ],
    operationalStates: partner?.operationalStateCodes
      ? partner.operationalStateCodes.map(
          (stateCode) => getStateByStateCode(stateCode) || { code: stateCode, name: '' }
        )
      : [],
    type: partner?.type || '',
    taxPersonType: partner?.taxInfo?.personType || '',
    companyName: partner?.taxInfo?.companyName || '',
    businessName: partner?.taxInfo?.businessName || '',
    taxId: partner?.taxInfo?.rfc || '',
    curp: partner?.taxInfo?.curp || '',
    taxRegime: partner?.taxInfo?.taxRegime || '',
    proofOfTaxSituation: partner?.taxInfo?.proofOfTaxSituation || '',
    repseId: partner?.taxInfo?.repseID || '',
    repseLink: partner?.taxInfo?.repseLink || '',
    usePartnerName: false,
    taxIdDocument: {
      name: taxIdDocument?.filename || '',
      path: taxIdDocument?.url || '',
      file: '',
      id: taxIdDocument?.id || ''
    },
    curpDocument: {
      name: curpDocument?.filename || '',
      path: curpDocument?.url || '',
      file: '',
      id: curpDocument?.id || ''
    }
  }

  const partnerId = searchParams.get('partnerId')

  return (
    <Formik
      onSubmit={isEdit || !!partnerId ? handleEditSubmit : handleCreateSubmit}
      initialValues={initialValues}
      validationSchema={schema(intl)}
    >
      {({ handleSubmit, dirty, isSubmitting }): ReactElement => {
        const isCreating = !isEdit && !partnerId
        const isEditing = !isCreating && dirty

        return (
          <FormLayout.Content as="form" onSubmit={handleSubmit}>
            <PartnerDataFormCard />
            <TaxComplianceFormCard />
            {addPartnerDataError && (
              <FormLayout.Actions>
                <ErrorMessage errors={getUnprocessableErrors(addPartnerDataError)}>
                  <FormattedMessage
                    id="partners.new.data.error"
                    defaultMessage="There was an error while adding the partner data"
                  />
                </ErrorMessage>
              </FormLayout.Actions>
            )}
            {changePartnerTaxInformationError && (
              <FormLayout.Actions>
                <ErrorMessage errors={getUnprocessableErrors(changePartnerTaxInformationError)}>
                  <FormattedMessage
                    id="partners.new.tax.error"
                    defaultMessage="There was an error while adding the tax information"
                  />
                </ErrorMessage>
              </FormLayout.Actions>
            )}
            {changePartnerDetailsError && (
              <FormLayout.Actions>
                <ErrorMessage errors={getUnprocessableErrors(changePartnerDetailsError)}>
                  <FormattedMessage
                    id="partners.new.details.error"
                    defaultMessage="There was an error while adding the partner details"
                  />
                </ErrorMessage>
              </FormLayout.Actions>
            )}
            {s3Error && (
              <FormLayout.Actions>
                <ErrorMessage>{s3Error}</ErrorMessage>
              </FormLayout.Actions>
            )}
            <FormLayout.Actions>
              <Button type="button" kind="cancel" onClick={onCancel} disabled={isSubmitting}>
                <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
              </Button>
              <Button type="submit" kind="primary" loading={isSubmitting}>
                {isCreating && (
                  <FormattedMessage id="partners.new.step1.createAndContinue" defaultMessage="Create & Continue" />
                )}
                {isEditing && (
                  <FormattedMessage id="partners.new.step1.saveAndContinue" defaultMessage="Save & Continue" />
                )}
                {!isCreating && !isEditing && (
                  <FormattedMessage id="partners.new.step1.Continue" defaultMessage="Continue" />
                )}
              </Button>
            </FormLayout.Actions>
          </FormLayout.Content>
        )
      }}
    </Formik>
  )
}
